/**
 * Hook for managing notifications - Iteration 6 - Notifications & Sharing.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/useAuth';
import { useLanguage } from '../i18n/useLanguage';
import * as notificationsApi from '../api/notifications';
import type { Notification } from '../types/notification';

interface UseNotificationsResult {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearAll: () => Promise<void>;
  refresh: () => Promise<void>;
}

const POLLING_INTERVAL = 30000; // 30 seconds
const PAGE_SIZE = 10;

/**
 * Hook for fetching and managing user notifications.
 * Includes polling for unread count and pagination support.
 */
export const useNotifications = (): UseNotificationsResult => {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);

  /**
   * Fetch unread count from the API.
   */
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const count = await notificationsApi.getUnreadCount();
      setUnreadCount(count);
    } catch (err: any) {
      // Only log non-network errors (network errors are expected when backend is down)
      if (err?.response) {
        console.error('Error fetching unread count:', err);
      }
      // Network errors are handled silently - count will remain at 0
    }
  }, [isAuthenticated]);

  /**
   * Fetch notifications from the API.
   */
  const fetchNotifications = useCallback(async (pageNum: number = 0, append: boolean = false) => {
    if (!isAuthenticated) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await notificationsApi.getNotifications(pageNum, PAGE_SIZE);
      
      if (append) {
        setNotifications(prev => [...prev, ...response.content]);
      } else {
        setNotifications(response.content);
      }
      
      setHasMore(!response.last);
      setPage(pageNum);
    } catch (err: any) {
      // Only log/show error for actual server errors, not network errors
      if (err?.response) {
        console.error('Error fetching notifications:', err);
        setError(t('notifications.error.load'));
      }
      // Network errors (backend down) are handled silently
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, t]);

  /**
   * Load more notifications (pagination).
   */
  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;
    await fetchNotifications(page + 1, true);
  }, [fetchNotifications, isLoading, hasMore, page]);

  /**
   * Mark a notification as read.
   */
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationsApi.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      // Only log actual server errors, not network errors
      if (err?.response) {
        console.error('Error marking notification as read:', err);
      }
      throw err; // Re-throw so caller can handle
    }
  }, []);

  /**
   * Mark all notifications as read.
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsApi.markAllAsRead();
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err: any) {
      // Only log actual server errors, not network errors
      if (err?.response) {
        console.error('Error marking all notifications as read:', err);
      }
      throw err; // Re-throw so caller can handle
    }
  }, []);

  /**
   * Delete all notifications.
   */
  const clearAll = useCallback(async () => {
    try {
      await notificationsApi.deleteAll();
      
      // Update local state
      setNotifications([]);
      setUnreadCount(0);
      setPage(0);
      setHasMore(false);
    } catch (err: any) {
      // Only log actual server errors, not network errors
      if (err?.response) {
        console.error('Error deleting all notifications:', err);
      }
      throw err; // Re-throw so caller can handle
    }
  }, []);

  /**
   * Refresh notifications (reset to first page).
   */
  const refresh = useCallback(async () => {
    await fetchNotifications(0, false);
    await fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
    } else {
      setUnreadCount(0);
      setNotifications([]);
    }
  }, [isAuthenticated, fetchUnreadCount]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(fetchUnreadCount, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    clearAll,
    refresh,
  };
};

export default useNotifications;
